/**
 * ResumableUploadZone — PR 20.
 *
 * Drag-and-drop / picker UI on top of useResumableUpload. Supports a single
 * file (multi-file uploads in one zone is out of scope for now; the wizard
 * step that needs many MSG files renders this component once per file).
 *
 * Surfaces:
 *   - Empty state: drop target with "browse" link.
 *   - In-progress: filename + progress bar + bytes/total.
 *   - Completed: green check + finalized storage path.
 *   - Error: rose banner + retry / abort.
 *
 * Calls `onComplete(session)` once the server returns `state='finalized'`.
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useResumableUpload } from '../../hooks/useResumableUpload';
import type { UploadSession, UploadSourceKind } from '../../types/migration.types';

interface Props {
  kind: UploadSourceKind;
  accept?: string;                 // file picker accept attribute
  helpText?: string;
  onComplete?: (session: UploadSession) => void;
  onClear?: () => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function ResumableUploadZone({ kind, accept, helpText, onComplete, onClear }: Props) {
  const { state, start, abort } = useResumableUpload();
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const completedRef = useRef<string | null>(null);

  const beginUpload = useCallback(async (file: File) => {
    completedRef.current = null;
    const session = await start({ file, kind });
    if (session && completedRef.current !== session.session_id) {
      completedRef.current = session.session_id;
      onComplete?.(session);
    }
  }, [start, kind, onComplete]);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    void beginUpload(files[0]);
  }, [beginUpload]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleClear = useCallback(() => {
    void abort();
    if (inputRef.current) inputRef.current.value = '';
    onClear?.();
  }, [abort, onClear]);

  // ─── render ─────────────────────────────────────────────────────────

  if (state.phase === 'done' && state.session) {
    return (
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-3 flex items-start gap-3">
        <CheckCircleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-emerald-900 dark:text-emerald-100 truncate">
            {state.session.filename}
          </div>
          <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
            {formatBytes(state.session.total_size)} uploaded.
            {state.session.sha256_actual && (
              <> SHA-256 <code className="font-mono">{state.session.sha256_actual.slice(0, 12)}…</code></>
            )}
          </div>
          {state.session.finalized_path && (
            <div className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80 mt-0.5 font-mono truncate">
              {state.session.finalized_path}
            </div>
          )}
        </div>
        <button onClick={handleClear} className="text-emerald-700 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (state.phase === 'uploading' || state.phase === 'finalizing' || state.phase === 'initializing') {
    const pct = Math.round(state.progress * 100);
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {state.filename || 'Uploading…'}
          </div>
          <button
            onClick={handleClear}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            title="Cancel upload"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
          <div
            className="h-full bg-lex-accent transition-all duration-150"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
          <span>
            {state.phase === 'finalizing' ? 'Finalizing…' :
             state.phase === 'initializing' ? 'Starting upload…' :
             `${formatBytes(state.bytesReceived)} / ${formatBytes(state.totalBytes)}`}
          </span>
          <span>{pct}%</span>
        </div>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className="rounded-lg border border-rose-200 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/20 p-3 flex items-start gap-3">
        <XCircleIcon className="h-5 w-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-rose-900 dark:text-rose-100">
            Upload failed
          </div>
          <div className="text-xs text-rose-700 dark:text-rose-300 mt-0.5">
            {state.error || 'Unknown error'}
          </div>
        </div>
        <button onClick={handleClear} className="text-xs px-2 py-1 rounded bg-rose-100 dark:bg-rose-800 text-rose-700 dark:text-rose-200 hover:bg-rose-200 dark:hover:bg-rose-700">
          Reset
        </button>
      </div>
    );
  }

  // Empty / aborted / idle.
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
        isDragging
          ? 'border-lex-accent bg-lex-accent-soft'
          : 'border-gray-300 dark:border-gray-700 hover:border-lex-accent hover:bg-lex-accent-soft/30'
      }`}
    >
      <ArrowUpTrayIcon className="h-7 w-7 mx-auto text-gray-400 dark:text-gray-500" />
      <div className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        Drop file here or <span className="text-lex-accent">browse</span>
      </div>
      {helpText && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helpText}</div>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

export default ResumableUploadZone;
